/**
 * Maps a funnel key to a plan-card layout. Adding a new setup = one entry
 * here; no routing changes.
 *
 * `satisfies` keeps the literal keys inferred (so `PlansVariantKey` stays
 * exact) while still type-checking every value.
 */
const plansVariantRegistry = {
  default: true,
  v2: true,
} satisfies Record<string, true>;

/** Every registered plan-card layout key, as a literal union. */
export type PlansVariantKey = keyof typeof plansVariantRegistry;

/** All registered plan-card layout keys. Source of truth is the registry. */
export const PLANS_VARIANT_KEYS = Object.keys(plansVariantRegistry) as PlansVariantKey[];

/** Narrow an arbitrary param value to a known plan-card layout key. */
export function isPlansVariant(key: string): key is PlansVariantKey {
  return key in plansVariantRegistry;
}

/** Layout used when the funnel names none, or names an unknown one. */
export const DEFAULT_PLANS_VARIANT: PlansVariantKey = 'default';

/** Param key that carries the plan-card layout selector (mirrors `checkout_v` / `quiz_v`). */
const VARIANT_PARAM_KEYS = ['plans_v'] as const;

/** The plan-card layout requested by the funnel params, if any. */
export function getRequestedPlansVariant(
  params: Record<string, string | undefined>,
): string | null {
  for (const key of VARIANT_PARAM_KEYS) {
    const value = params[key];
    if (value && value.trim()) return value.trim();
  }
  return null;
}

export interface ResolvedPlansVariant {
  /** Registry key of the resolved layout. */
  id: PlansVariantKey;
}

/**
 * Resolve which plan-card layout to render from the effective funnel params:
 * an explicit `plans_v` wins; otherwise the default. Pure.
 */
export function resolvePlansVariant(
  params: Record<string, string | undefined>,
): ResolvedPlansVariant {
  const requested = getRequestedPlansVariant(params);
  if (requested && isPlansVariant(requested)) {
    return { id: requested };
  }
  return { id: DEFAULT_PLANS_VARIANT };
}
